import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useKeycloak } from "@react-keycloak/web";
import { FaDownload, FaPlus, FaRegTrashAlt } from "react-icons/fa";
import { IoMdCheckboxOutline } from "react-icons/io";
import { CgCloseR } from "react-icons/cg";
import { LuSparkles } from "react-icons/lu";
import Swal from "sweetalert2";
import { v4 as uuidv4 } from 'uuid';

import Loader from "../../Atoms/Loader";
import Container from "../../Atoms/Container";
import ButtonMain from "../../Atoms/ButtonMain";
import ButtonSecondary from "../../Atoms/ButtonSecondary";

import IdeaCard from "./Molecules/IdeaCard";
import EditIdeaModal from "./Molecules/EditIdeaModal";
import NewIdeaModal from "./Molecules/NewIdeaModal";
import KnowledgeBaseModal from "./Molecules/KnowledgeBaseModal";
import IdeasToConceptsModal from "./Molecules/IdeasToConceptsModal";
import TooMuchIdeasModal from "./Molecules/TooMuchIdeasModal";

import useBrainstormStore from "../../../hooks/useBrainstormStore";
import AnimatedBrainLoader from "../../Atoms/AnimatedBrainLoader";
import { createBrainstorm, generateIdeasByGuidingQuestion, renderIdeasPDF, createAssetKnowledgeBase } from "../../../services/Brainstorm/BrainstormService";

import './styles.css';

export default function BrainstormIdeasTemplate() {
    const { projectId } = useParams();
    const { keycloak } = useKeycloak();
    const { state } = useLocation();
    const navigate = useNavigate();
    const KEYCLOACK_TOKEN = keycloak.token;

    const [selectedGuidingQuestion, setSelectedGuidingQuestion] = useState(null);
    const [currentBrainstorm, setCurrentBrainstorm] = useState(null);
    const [guidingQuestions, setGuidingQuestions] = useState([]);
    const [ideas, setIdeas] = useState([]);
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingIdea, setEditingIdea] = useState(null);
    const [showNewIdeaModal, setShowNewIdeaModal] = useState(false);
    const [showKnowledgeBaseModal, setShowKnowledgeBaseModal] = useState(false);
    const [showIdeasToConceptsModal, setShowIdeasToConceptsModal] = useState(false);
    const [showTooMuchIdeasModal, setShowTooMuchIdeasModal] = useState(false);
    const [isGeneratingMoreIdeas, setIsGeneratingMoreIdeas] = useState(false);
    const [isDownloadDisabled, setIsDownloadDisabled] = useState(true);
    const [loadingPDF, setLoadingPDF] = useState(false);
    const [loadingKnowledgeBase, setLoadingKnowledgeBase] = useState(false);

    const { 
        getBrainstormsByProject,
        deleteBrainstormByProjectId,
        getIdeasByGuidingQuestion, 
        getGuidingQuestionsByChallenge,
        generateInitialIdeas: generateInitialIdeasStore,
        updateGuidingQuestion,
        updateIdeaInGuidingQuestion,
        updateMultipleIdeasInGuidingQuestion,
        addIdeaToGuidingQuestion,
        deleteIdeaFromGuidingQuestion,
        deleteMultipleIdeas,
        getIdeaById,
        addMultipleIdeasToGuidingQuestion,
        toggleGuidingQuestionKnowledgeBase,
        changeBrainstormStep
    } = useBrainstormStore();

    useEffect(() => {
        // checkPageAccess();
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId]);

    useEffect(() => {
        const latestBrainstorm = getBrainstormsByProject(parseInt(projectId))[0];
        
        if (!latestBrainstorm) {
            setIsDownloadDisabled(true);
            return;
        }

        // Button is disabled only when ALL guiding questions have 0 selected ideas
        const hasAnySelectedIdeas = latestBrainstorm.guiding_questions?.some(
            question => question.ideas?.some(idea => idea.selected === true)
        ) || false;
        
        setIsDownloadDisabled(!hasAnySelectedIdeas);
    }, [projectId, currentBrainstorm?.updatedAt, getBrainstormsByProject]);

    // const checkPageAccess = () => {
    //     const currentBrainstormStore = getBrainstormsByProject(parseInt(projectId));
    //     const brainstorm = currentBrainstormStore[0];

    //     const hasGuidingQuestions = brainstorm.guiding_questions.length > 0;

    //     if (!hasGuidingQuestions) {
    //         navigate(`/brainstorm/${projectId}`);
    //     }
    // }

    const loadData = async () => {
        if (isLoadingData) {
            console.log('Carregamento em andamento...');
            return;
        }

        setIsLoadingData(true);
        
        try {
            const currentBrainstormStore = getBrainstormsByProject(parseInt(projectId));

            if (!currentBrainstormStore || currentBrainstormStore.length === 0) {
                console.warn('No brainstorm found for project:', projectId);
                return;
            }

            const brainstorm = currentBrainstormStore[0];
            setCurrentBrainstorm(brainstorm);
            
            const selectedChallenge = brainstorm.challenges.find(challenge => challenge.selected === true);

            if (!selectedChallenge) {
                console.warn('No selected challenge found');
                return;
            }

            const challengeGuidingQuestions = getGuidingQuestionsByChallenge(brainstorm.id, selectedChallenge.id);
            const selectedGuidingQuestions = challengeGuidingQuestions.filter(question => question.selected === true);
            setGuidingQuestions(selectedGuidingQuestions);
            
            if (selectedGuidingQuestions.length === 0) {
                console.warn('No guiding questions found for selected challenge');
                return;
            }

            const firstQuestion = selectedGuidingQuestions[0];
            setSelectedGuidingQuestion(firstQuestion.id);
            
            if (!state?.isUpdating) {
                //If the user is creating a new work, Clear existing ideas for selected guiding questions
                selectedGuidingQuestions.forEach(question => {
                    updateGuidingQuestion(brainstorm.id, question.id, { ideas: [] });
                });
            }
            
            // Generate ideas for ALL selected guiding questions at once
            if (state?.generateIdeas) {
                try {
                    const ideasCount = state?.ideasPerQuestion || 5;
                    await generateInitialIdeasStore(
                        brainstorm.id,
                        parseInt(projectId),
                        KEYCLOACK_TOKEN,
                        ideasCount,
                    );
                    
                    const updatedBrainstorm = getBrainstormsByProject(parseInt(projectId))[0];
                    if (updatedBrainstorm) {
                        setCurrentBrainstorm(updatedBrainstorm);
                        
                        const updatedFirstQuestion = updatedBrainstorm.guiding_questions.find(q => q.id === firstQuestion.id);
                        if (updatedFirstQuestion && updatedFirstQuestion.ideas) {
                            setIdeas(updatedFirstQuestion.ideas);
                        }
                    }
    
                    console.log('✅ All guiding questions now have ideas generated');
                } catch (error) {
                    console.error('Error generating ideas:', error);
                    Swal.fire({
                        title: 'Ocorreu um erro',
                        text: 'Erro ao gerar ideias. Tente novamente mais tarde',
                        timer: 1800,
                        showConfirmButton: false,
                        background: '#27272A',
                        color: '#fff',
                        position: 'top-end',
                    });
                }
            } else {
                const selectedQuestionIdeas = getIdeasByGuidingQuestion(brainstorm.id, firstQuestion.id);
                setIdeas(selectedQuestionIdeas || []);
            }
        } catch (error) {
            console.error('Error in loadData:', error);
            Swal.fire({
                title: 'Ocorreu um erro',
                text: 'Erro ao gerar ideias. Tente novamente mais tarde',
                timer: 1800,
                showConfirmButton: false,
                background: '#27272A',
                color: '#fff',
                position: 'top-end',
            });
        } finally {
            setIsLoadingData(false);
        }
    }

    const handleAddNewIdea = () => {
        setShowNewIdeaModal(true);
    }

    const handleAddMoreIdeas = async () => {
        try {
            if (!selectedGuidingQuestion) {
                Swal.fire({
                    title: 'Nenhuma questão selecionada',
                    text: 'Selecione uma questão norteadora para gerar mais ideias.',
                    timer: 2000,
                    showConfirmButton: false,
                    background: '#27272A',
                    color: '#fff',
                    position: 'top-end',
                });
                return;
            }

            const currentGuidingQuestion = guidingQuestions.find(q => q.id === selectedGuidingQuestion);
            if (!currentGuidingQuestion) {
                throw new Error('Guiding question not found');
            }

            const challenge = currentBrainstorm.challenges.find(c => c.id === currentGuidingQuestion.challenge_id);
            const challengeText = challenge?.text || '';

            setIsGeneratingMoreIdeas(true);

            const apiResponse = await generateIdeasByGuidingQuestion(
                parseInt(projectId),
                currentGuidingQuestion.text,
                challengeText,
                6, // Generate 6 ideas
                ideas || [],
                KEYCLOACK_TOKEN,
            );

            const generatedIdeas = apiResponse?.response?.ideias || [];
            
            const formattedIdeas = generatedIdeas.map((idea, index) => ({
                id: uuidv4(),
                title: idea.titulo_da_ideia || `Ideia ${index + 1}`,
                description: idea.descricao_da_ideia || '',
                createdAt: new Date().toISOString(),
                source: 'api_generated',
                selected: false, // New ideas start unselected
                guidingQuestionId: currentGuidingQuestion.id
            }));

            const addedIdeas = addMultipleIdeasToGuidingQuestion(
                currentBrainstorm.id,
                currentGuidingQuestion.id,
                formattedIdeas
            );

            const updatedBrainstorm = getBrainstormsByProject(parseInt(projectId))[0];
            if (updatedBrainstorm) {
                setCurrentBrainstorm(updatedBrainstorm);
                
                const updatedIdeas = getIdeasByGuidingQuestion(updatedBrainstorm.id, currentGuidingQuestion.id);
                setIdeas(updatedIdeas);
            }

            const successMessage = addedIdeas.length < formattedIdeas.length
                ? `${addedIdeas.length} novas ideias foram adicionadas (limite de 38 ideias por questão norteadora).`
                : `${addedIdeas.length} novas ideias foram adicionadas.`;

            Swal.fire({
                title: 'Ideias geradas!',
                text: successMessage,
                timer: 1500,
                showConfirmButton: false,
                background: '#27272A',
                color: '#fff',
                position: 'top-end',
            });

        } catch (error) {
            console.error('Error generating more ideas:', error);
            const errorMessage = error.message?.includes('Maximum limit of 38 ideas')
                ? 'Limite máximo de 38 ideias por questão norteadora foi atingido.'
                : 'Ocorreu um erro ao gerar mais ideias. Tente novamente.';
            
            Swal.fire({
                title: 'Erro ao gerar ideias',
                text: errorMessage,
                timer: 2000,
                showConfirmButton: false,
                background: '#27272A',
                color: '#fff',
                position: 'top-end',
            });
        } finally {
            setIsGeneratingMoreIdeas(false);
        }
    };

    const handleNewIdeaConfirm = (newIdea) => {
        try {
            const currentGuidingQuestion = guidingQuestions.find(q => q.id === selectedGuidingQuestion);
            if (!currentGuidingQuestion) {
                throw new Error('Guiding question not found');
            }

            addIdeaToGuidingQuestion(
                currentBrainstorm.id,
                currentGuidingQuestion.id,
                {
                    title: newIdea.title,
                    description: newIdea.description,
                    selected: false,
                    source: 'user_created'
                }
            );

            const updatedBrainstorm = getBrainstormsByProject(parseInt(projectId))[0];
            if (updatedBrainstorm) {
                setCurrentBrainstorm(updatedBrainstorm);
                
                const updatedIdeas = getIdeasByGuidingQuestion(updatedBrainstorm.id, currentGuidingQuestion.id);
                setIdeas(updatedIdeas);
            }

            Swal.fire({
                title: 'Ideia criada!\u2728',
                text: 'A ideia foi adicionada com sucesso.',
                timer: 1500,
                showConfirmButton: false,
                background: '#27272A',
                color: '#fff',
                position: 'top-end',
            });
        } catch (error) {
            console.error('Error creating idea:', error);
            const errorMessage = error.message?.includes('Maximum limit of 38 ideas')
                ? 'Limite máximo de 38 ideias por questão norteadora foi atingido.'
                : 'Ocorreu um erro ao criar a ideia. Tente novamente.';
            
            Swal.fire({
                title: 'Erro ao criar ideia',
                text: errorMessage,
                timer: 2000,
                showConfirmButton: false,
                background: '#27272A',
                color: '#fff',
                position: 'top-end',
            });
        } finally {
            setShowNewIdeaModal(false);
        }
    };

    const handleNewIdeaCancel = () => {
        setShowNewIdeaModal(false);
    };

    const handleDeleteSelectedIdeas = async () => {
        try {
            const selectedIdeas = ideas.filter(idea => idea.selected);
            
            if (selectedIdeas.length === 0) {
                Swal.fire({
                    title: 'Nenhuma ideia selecionada',
                    text: 'Selecione pelo menos uma ideia para excluir.',
                    timer: 2000,
                    showConfirmButton: false,
                    background: '#27272A',
                    color: '#fff',
                    position: 'top-end',
                });
                return;
            }
            
            const result = await Swal.fire({
                title: 'Confirmar exclusão em massa',
                text: `Tem certeza que deseja excluir ${selectedIdeas.length} ideia(s) selecionada(s)?`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#ff6b6b',
                cancelButtonColor: '#6c757d',
                confirmButtonText: 'Sim, excluir todas!',
                cancelButtonText: 'Cancelar',
                background: '#27272A',
                color: '#fff',
            });
            
            if (result.isConfirmed) {
                const currentGuidingQuestion = guidingQuestions.find(q => q.id === selectedGuidingQuestion);
                
                if (!currentGuidingQuestion) {
                    throw new Error('Guiding question not found');
                }
                
                const selectedIdeaIds = selectedIdeas.map(idea => idea.id);
                
                deleteMultipleIdeas(
                    currentBrainstorm.id,
                    currentGuidingQuestion.id,
                    selectedIdeaIds
                );
                
                const updatedBrainstorm = getBrainstormsByProject(parseInt(projectId))[0];
                if (updatedBrainstorm) {
                    setCurrentBrainstorm(updatedBrainstorm);
                    
                    const updatedIdeas = getIdeasByGuidingQuestion(updatedBrainstorm.id, currentGuidingQuestion.id);
                    setIdeas(updatedIdeas);
                }
                
                Swal.fire({
                    title: 'Ideias excluídas!',
                    text: `${selectedIdeas.length} ideia(s) foram excluídas com sucesso.`,
                    timer: 1500,
                    showConfirmButton: false,
                    background: '#27272A',
                    color: '#fff',
                    position: 'top-end',
                });
            }
            
        } catch (error) {
            console.error('Error deleting selected ideas:', error);
            Swal.fire({
                title: 'Erro ao excluir ideias',
                text: 'Ocorreu um erro ao excluir as ideias selecionadas. Tente novamente.',
                timer: 2000,
                showConfirmButton: false,
                background: '#27272A',
                color: '#fff',
                position: 'top-end',
            });
        }
    };

    const handleGuidingQuestionSelect = (index, question) => {
        const ideas = getIdeasByGuidingQuestion(currentBrainstorm?.id, question.id);
        setIdeas(ideas);

        setSelectedGuidingQuestion(selectedGuidingQuestion === question.id ? null : question.id);
    };

    const handleIdeaSelect = (ideaId) => {
        const idea = getIdeaById(ideaId);
        if (idea) {
            updateIdeaInGuidingQuestion(currentBrainstorm?.id, selectedGuidingQuestion, idea.id, { selected: !idea.selected });
            
            const updatedBrainstorm = getBrainstormsByProject(parseInt(projectId))[0];
            if (updatedBrainstorm) {
                setCurrentBrainstorm(updatedBrainstorm);
                
                const updatedIdeas = getIdeasByGuidingQuestion(updatedBrainstorm.id, selectedGuidingQuestion);
                setIdeas(updatedIdeas);
            }
        }
    };

    const handleSelectAll = () => {
        updateMultipleIdeasInGuidingQuestion(currentBrainstorm?.id, selectedGuidingQuestion, { selected: true });
        
        const updatedBrainstorm = getBrainstormsByProject(parseInt(projectId))[0];
        if (updatedBrainstorm) {
            setCurrentBrainstorm(updatedBrainstorm);
            
            const updatedIdeas = getIdeasByGuidingQuestion(updatedBrainstorm.id, selectedGuidingQuestion);
            setIdeas(updatedIdeas);
        }
    };

    const handleDownloadSelected = async () => {
        setLoadingPDF(true);
        const currentChallenge = currentBrainstorm.challenges.find(c => c.selected === true);
        if (!currentChallenge) {
            setIsDownloadDisabled(true);
            return;
        }

        const currentGuidingQuestion = guidingQuestions.find(q => q.id === selectedGuidingQuestion);
        if (!currentGuidingQuestion) {
            setIsDownloadDisabled(true);
            return;
        }

        const formattedGuidingQuestions = currentBrainstorm.guiding_questions
        .filter(question => question.ideas.some(idea => idea.selected === true))
        .map(q => ({
            ...q,
            ideas: q.ideas.filter(idea => idea.selected === true)
        }));

        const payload = {
            templateName: 'brainstorm',
            challenge: currentChallenge.text,
            blocks: formattedGuidingQuestions,
            fileName: 'ideias-brainstorm'
        }
        
        const pdfFromServer = await renderIdeasPDF(payload,  KEYCLOACK_TOKEN);

        if (pdfFromServer) {
            const byteArray = new Uint8Array(Object.values(pdfFromServer));

            const blob = new Blob([byteArray], { type: "application/pdf" });

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${payload.fileName}.pdf`;
            document.body.appendChild(a);
            a.click();

             document.body.removeChild(a);
             window.URL.revokeObjectURL(url);    
        }

        setLoadingPDF(false);
    };

    const handleCompleteSelection = () => {
        const totalSelectedIdeas = currentBrainstorm?.guiding_questions?.reduce((total, question) => {
            const selectedCount = question.ideas?.filter(idea => idea.selected === true).length || 0;
            return total + selectedCount;
        }, 0) || 0;

        if (totalSelectedIdeas <= 20) {
            setShowIdeasToConceptsModal(true);
        } else {
            setShowTooMuchIdeasModal(true);
        }
    };

    const handleSaveOnlyIdeas = () => {
        setShowIdeasToConceptsModal(false);
        setShowTooMuchIdeasModal(false);
        setShowKnowledgeBaseModal(true);
    };

    const handleGenerateConcepts = () => {
        changeBrainstormStep(currentBrainstorm.id, 3);
        navigate(`/brainstorm/concepts/${projectId}`);
    };

    const handleReviewSelection = () => {
        setShowTooMuchIdeasModal(false);
    };

    const handleToggleKnowledgeBase = (brainstormId, questionId) => {
        toggleGuidingQuestionKnowledgeBase(brainstormId, questionId);
        
        const updatedBrainstorm = getBrainstormsByProject(parseInt(projectId))[0];
        if (updatedBrainstorm) {
            setCurrentBrainstorm(updatedBrainstorm);
        }
    };

    const generateNewFileName = () => {
        const date = new Date();
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const day = date.getDate();
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const seconds = date.getSeconds();

        return 'brainstorm_' + year + month + day + '_' + hours + minutes + seconds;
    }

    const handleFinishKnowledgeBaseReview = async () => {
        try {
            setLoadingKnowledgeBase(true);
            
            const latestBrainstormForPayload = getBrainstormsByProject(parseInt(projectId))[0];
            if (!latestBrainstormForPayload) {
                Swal.fire({
                    icon: 'error',
                    title: 'Erro ao finalizar trabalho',
                    text: 'Erro ao buscar dados atualizados. Tente novamente.',
                    timer: 2000,
                    showConfirmButton: false,
                    background: '#27272A',
                    color: '#fff',
                    position: 'top-end',
                });
                return;
            }
            
            const allBrainstormIdeas = latestBrainstormForPayload.guiding_questions.map(question => question.ideas);
            const payload = {
                challenges: latestBrainstormForPayload.challenges,
                guidingQuestions: {...latestBrainstormForPayload.guiding_questions},
                ideas: {...allBrainstormIdeas.flat()},
                groups: {...latestBrainstormForPayload.groups},
                concepts: {...latestBrainstormForPayload.concepts}
            }

            const newBrainstorm = await createBrainstorm(projectId, payload, KEYCLOACK_TOKEN);

            if (newBrainstorm) {
                const currentChallenge = latestBrainstormForPayload.challenges.find(c => c.selected === true);
                if (!currentChallenge) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Erro ao finalizar trabalho',
                        text: 'Erro ao identificar desafio principal. Tente novamente.',
                    });
                    return;
                }

                const currentGuidingQuestion = guidingQuestions.find(q => q.id === selectedGuidingQuestion);
                if (!currentGuidingQuestion) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Erro ao finalizar trabalho',
                        text: 'Erro ao identificar questão norteadora. Tente novamente.',
                    });
                    return;
                }

                const latestBrainstorm = getBrainstormsByProject(parseInt(projectId))[0];
                if (!latestBrainstorm) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Erro ao finalizar trabalho',
                        text: 'Erro ao buscar dados atualizados. Tente novamente.',
                        timer: 2000,
                        showConfirmButton: false,
                        background: '#27272A',
                        color: '#fff',
                        position: 'top-end',
                    });
                    return;
                }

                const formattedGuidingQuestions = latestBrainstorm.guiding_questions
                .filter(question => question.in_knowledge_base && question.ideas.some(idea => idea.selected))
                .map(q => ({
                    ...q,
                    ideas: q.ideas.filter(idea => idea.selected)
                }));

                const newFileName = generateNewFileName();

                const assetPayload = {
                    templateName: 'brainstorm',
                    challenge: currentChallenge.text,
                    blocks: formattedGuidingQuestions,
                    fileName: newFileName
                }

                const assetKnowledgeBase = await createAssetKnowledgeBase(projectId, assetPayload, newFileName, KEYCLOACK_TOKEN);
                if (assetKnowledgeBase) {
                    Swal.fire({
                        title: 'Trabalho finalizado!',
                        text: 'O trabalho foi finalizado com sucesso.',
                        timer: 1500,
                        showConfirmButton: false,
                        background: '#27272A',
                        color: '#fff',
                        position: 'top-end',
                    });

                    navigate(`/project/${projectId}`);
                } else {
                    Swal.fire({
                        title: 'Erro ao criar asset da base de conhecimento',
                        text: 'Ocorreu um erro ao criar o asset da base de conhecimento. Tente novamente.',
                        timer: 2000,
                        showConfirmButton: false,
                        background: '#27272A',
                        color: '#fff',
                        position: 'top-end',
                    });
                    return;
                }
            } else {
                Swal.fire({
                    title: 'Erro ao finalizar trabalho',
                    text: 'Ocorreu um erro ao concluir a finalização do trabalho. Tente novamente.',
                    timer: 2000,
                    showConfirmButton: false,
                    background: '#27272A',
                    color: '#fff',
                    position: 'top-end',
                });
                return;
            }

        } catch (error) {
            console.error('Error finishing knowledge base review:', error);
            Swal.fire({
                title: 'Erro ao finalizar trabalho',
                text: 'Ocorreu um erro ao concluir a revisão da base de conhecimento. Tente novamente.',
                timer: 2000,
                showConfirmButton: false,
                background: '#27272A',
                color: '#fff',
                position: 'top-end',
            });
        } finally {
            deleteBrainstormByProjectId(projectId);
            setShowKnowledgeBaseModal(false);
            setLoadingKnowledgeBase(false);
        }
    };

    const handleEditIdea = (ideaId) => {
        const idea = ideas.find(i => i.id === ideaId);
        if (idea) {
            setEditingIdea(idea);
            setShowEditModal(true);
        }
    };
    
    const handleEditConfirm = (editedIdea) => {
        try {
            const currentGuidingQuestion = guidingQuestions.find(q => q.id === selectedGuidingQuestion);
            
            if (!currentGuidingQuestion) {
                throw new Error('Guiding question not found');
            }
            
                updateIdeaInGuidingQuestion(
                    currentBrainstorm.id,
                currentGuidingQuestion.id,
                    editedIdea.id,
                    {
                        title: editedIdea.title,
                        description: editedIdea.description
                    }
                );
                
                const updatedBrainstorm = getBrainstormsByProject(parseInt(projectId))[0];
                if (updatedBrainstorm) {
                    setCurrentBrainstorm(updatedBrainstorm);
                    
                    const updatedIdeas = getIdeasByGuidingQuestion(updatedBrainstorm.id, currentGuidingQuestion.id);
                    setIdeas(updatedIdeas);
                }

                Swal.fire({
                    title: 'Ideia atualizada!',
                    text: 'A ideia foi atualizada com sucesso.',
                    timer: 1500,
                    showConfirmButton: false,
                    background: '#27272A',
                    color: '#fff',
                    position: 'top-end',
                });
            
        } catch (error) {
            console.error('Error updating idea:', error);
            Swal.fire({
                title: 'Erro ao atualizar ideia',
                text: 'Ocorreu um erro ao atualizar a ideia. Tente novamente.',
                timer: 2000,
                showConfirmButton: false,
                background: '#27272A',
                color: '#fff',
                position: 'top-end',
            });
        } finally {
            setShowEditModal(false);
            setEditingIdea(null);
        }
    };
    
    const handleEditCancel = () => {
        setShowEditModal(false);
        setEditingIdea(null);
    };
    
    const handleDeleteIdea = async (ideaId) => {
        try {
            const idea = getIdeaById(ideaId);
            const ideaTitle = idea?.title || 'esta ideia';
            
            const result = await Swal.fire({
                title: 'Confirmar exclusão',
                text: `Tem certeza que deseja excluir "${ideaTitle}"?`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#ff6b6b',
                cancelButtonColor: '#6c757d',
                confirmButtonText: 'Sim, excluir!',
                cancelButtonText: 'Cancelar',
                background: '#27272A',
                color: '#fff',
            });
            
            if (result.isConfirmed) {
                const currentGuidingQuestion = guidingQuestions.find(q => q.id === selectedGuidingQuestion);
                
                if (!currentGuidingQuestion) {
                    throw new Error('Guiding question not found');
                }
                
                deleteIdeaFromGuidingQuestion(
                    currentBrainstorm.id,
                    currentGuidingQuestion.id,
                    ideaId
                );
                
                const updatedBrainstorm = getBrainstormsByProject(parseInt(projectId))[0];
                if (updatedBrainstorm) {
                    setCurrentBrainstorm(updatedBrainstorm);
                    
                    const updatedIdeas = getIdeasByGuidingQuestion(updatedBrainstorm.id, currentGuidingQuestion.id);
                    setIdeas(updatedIdeas);
                }
                
                Swal.fire({
                    title: 'Ideia excluída!',
                    text: 'A ideia foi excluída com sucesso.',
                    timer: 1500,
                    showConfirmButton: false,
                    background: '#27272A',
                    color: '#fff',
                    position: 'top-end',
                });
            }
            
        } catch (error) {
            console.error('Error deleting idea:', error);
            Swal.fire({
                title: 'Erro ao excluir ideia',
                text: 'Ocorreu um erro ao excluir a ideia. Tente novamente.',
                timer: 2000,
                showConfirmButton: false,
                background: '#27272A',
                color: '#fff',
                position: 'top-end',
            });
        }
    };

    const countSelectedIdeas = currentBrainstorm?.guiding_questions?.reduce((total, question) => {
        const selectedCount = question.ideas?.filter(idea => idea.selected === true).length || 0;
        return total + selectedCount;
    }, 0) || 0;

    return (
        <>
            <EditIdeaModal
                show={showEditModal}
                onHide={handleEditCancel}
                idea={editingIdea}
                onConfirm={handleEditConfirm}
            />

            <NewIdeaModal
                show={showNewIdeaModal}
                onHide={handleNewIdeaCancel}
                onConfirm={handleNewIdeaConfirm}
            />

            <KnowledgeBaseModal
                show={showKnowledgeBaseModal}
                onHide={() => setShowKnowledgeBaseModal(false)}
                brainstormId={currentBrainstorm?.id}
                onToggleKnowledgeBase={handleToggleKnowledgeBase}
                handleConclude={handleFinishKnowledgeBaseReview}
                loading={loadingKnowledgeBase}
            />

            <IdeasToConceptsModal
                show={showIdeasToConceptsModal}
                onHide={() => setShowIdeasToConceptsModal(false)}
                onSaveOnly={handleSaveOnlyIdeas}
                onGenerateConcepts={handleGenerateConcepts}
            />

            <TooMuchIdeasModal
                show={showTooMuchIdeasModal}
                onHide={() => setShowTooMuchIdeasModal(false)}
                selectedCount={countSelectedIdeas}
                onReviewSelection={handleReviewSelection}
                onSaveOnly={handleSaveOnlyIdeas}
            />

            <Container>
            <section id="brainstorm-ideas-header" className="mb-3">
                    <div className="d-flex flex-row align-items-center justify-content-between">
                        <h3>Novas ideias geradas</h3>
                        
                        <div className="d-flex flex-row gap-2">
                            <ButtonSecondary
                                icon={loadingPDF ? <Loader size={20} /> : <FaDownload />}
                                onClick={handleDownloadSelected}
                                disabled={loadingPDF || isDownloadDisabled}
                            >
                                {loadingPDF ? "Preparando arquivo..." : 'Baixar selecionadas'}
                            </ButtonSecondary>
                            
                            <ButtonMain onClick={handleCompleteSelection} disabled={isLoadingData}>
                                Concluir seleção
                            </ButtonMain>
                        </div>
                    </div>
                </section>
            </Container>

            <div className="brainstorm-ideas-header" />

            <section id="brainstorm-ideas-content">
                <Container>
                    <div className="row brainstorm-ideas-layout">
                        {/* Left Column - Problem Statements */}
                        <div className="col-12 col-lg-4 brainstorm-ideas-left-column">
                            <div className="brainstorm-ideas-section-header">
                                <p>Como podemos...</p>
                            </div>
                            
                            <div className="brainstorm-ideas-scrollable-content">
                                {guidingQuestions.map((question, index) => (
                                    <div 
                                        key={index} 
                                        className={`problem-statement-item ${selectedGuidingQuestion === question.id ? 'selected' : ''}`}
                                        onClick={() => handleGuidingQuestionSelect(index, question)}
                                        role="button"
                                        tabIndex={0}
                                        aria-pressed={selectedGuidingQuestion === question.id}
                                    >
                                        <p>{question.text}</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Right Column - Idea Cards */}
                        <div className="col-12 col-lg-8 brainstorm-ideas-right-column">
                            <div className="brainstorm-ideas-section-header">
                                <p>{countSelectedIdeas}/20 Ideias selecionadas</p>
                                
                                <div className="brainstorm-ideas-actions">
                                    <button 
                                        className="btn"
                                        onClick={handleAddNewIdea}
                                        title="Adicionar nova ideia"
                                    >
                                        <FaPlus size={14} />
                                    </button>
                                    <button 
                                        className="btn"
                                        onClick={handleSelectAll}
                                        title="Selecionar todas"
                                    >
                                        <IoMdCheckboxOutline size={17} />
                                    </button>
                                    <button 
                                        className="btn"
                                        onClick={() => {
                                            updateMultipleIdeasInGuidingQuestion(currentBrainstorm?.id, selectedGuidingQuestion, { selected: false });
                                            
                                            const updatedBrainstorm = getBrainstormsByProject(parseInt(projectId))[0];
                                            if (updatedBrainstorm) {
                                                setCurrentBrainstorm(updatedBrainstorm);
                                                
                                                const updatedIdeas = getIdeasByGuidingQuestion(updatedBrainstorm.id, selectedGuidingQuestion);
                                                setIdeas(updatedIdeas);
                                            }
                                        }}
                                        title="Desmarcar todas"
                                    >
                                        <CgCloseR size={14} />
                                    </button>
                                    <button 
                                            className="btn"
                                        onClick={handleDeleteSelectedIdeas}
                                        title="Excluir selecionadas"
                                    >
                                        <FaRegTrashAlt size={14} />
                                    </button>
                                </div>
                            </div>
                            
                            <div className="brainstorm-ideas-scrollable-content">
                                <div className="ideas-grid">
                                    {isLoadingData ? (
                                        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                                            <div className="d-flex flex-column align-items-center justify-content-center">
                                                <AnimatedBrainLoader size={200} />
                                                <p className="text-secondary">A geração de ideias pode demorar alguns segundos...</p>
                                                <p>Dica: Ao finalizar o trabalho, o resultado estará disponível na aba Workspace, na página do projeto</p>
                                                
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            {ideas.map((idea) => (
                                                <IdeaCard
                                                    key={idea.id}
                                                    id={idea.id}
                                                    title={idea.title}
                                                    description={idea.description}
                                                    isSelected={idea.selected}
                                                    onSelect={handleIdeaSelect}
                                                    onEdit={handleEditIdea}
                                                    onEditConfirm={handleEditConfirm}
                                                    onDelete={handleDeleteIdea}
                                                />
                                            ))}

                                            <button 
                                                className="generate-more-ideas-button" 
                                                onClick={handleAddMoreIdeas}
                                                disabled={isGeneratingMoreIdeas}
                                            >
                                                <div
                                                    className="generate-more-ideas"
                                                    style={{ cursor: isGeneratingMoreIdeas ? 'not-allowed' : 'pointer' }}
                                                >
                                                    <div className="generate-more-content">
                                                        {isGeneratingMoreIdeas ? (
                                                            <p>
                                                                <Loader size={20} />
                                                            </p>
                                                        ) : (
                                                            <p><LuSparkles size={20} /> Gerar + 6</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </Container>
            </section>
        </>
    );
}